import React, { useEffect, useState } from 'react';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, AlertTriangle, Trash2, RefreshCw, MapPin, X, Phone, Mail, FileText, AlertCircle, CheckCircle, CalendarClock } from 'lucide-react';

const STATUSES = ['confirmed', 'in_progress', 'completed', 'cancelled', 'no_show'];

const AdminV2NotaryBookings = () => {
  const { authFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [calStatus, setCalStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rsDate, setRsDate] = useState('');
  const [rsTime, setRsTime] = useState('');
  const [rsNotify, setRsNotify] = useState(true);
  const [rsConflicts, setRsConflicts] = useState([]);
  const [rsErr, setRsErr] = useState('');
  const [cleaningCal, setCleaningCal] = useState(false);

  const loadCal = async () => {
    try {
      const r = await authFetch('/api/notary/google-calendar/status');
      if (r.ok) setCalStatus(await r.json());
    } catch {}
  };
  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/notary/admin/bookings');
      if (res.ok) {
        const d = await res.json();
        const list = d.bookings || d || [];
        setBookings(list);
        return list;
      } else setError('Failed to load bookings');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
    return null;
  };

  useEffect(() => { load(); loadCal(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/notary/admin/bookings/${id}/status?new_status=${status}`, { method: 'PATCH' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Update failed.');
      }
      setMsg({ type: 'green', text: 'Status updated.' });
      const list = await load();
      if (list) setSelected(list.find(b => b.id === id) || null);
    } catch (err) { setMsg({ type: 'red', text: err.message || 'Update failed.' }); }
    finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const syncCal = async (id) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/notary/admin/bookings/${id}/sync-calendar`, { method: 'POST' });
      if (res.ok) setMsg({ type: 'green', text: 'Synced to Google Calendar.' });
      else setMsg({ type: 'red', text: 'Sync failed.' });
    } finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    setActionLoading(id);
    try {
      await authFetch(`/api/notary/admin/bookings/${id}`, { method: 'DELETE' });
      setSelected(null);
      await load();
    } finally { setActionLoading(null); }
  };

  const cleanupCalendar = async () => {
    setCleaningCal(true);
    try {
      const res = await authFetch('/api/notary/admin/calendar/cleanup-cancelled', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) setMsg({ type: 'green', text: d.message || 'Calendar cleanup started.' });
      else setMsg({ type: 'red', text: d.detail || 'Cleanup failed.' });
    } catch (e) { setMsg({ type: 'red', text: 'Cleanup failed.' }); }
    finally { setCleaningCal(false); setTimeout(() => setMsg(null), 4000); }
  };

  // Open the inline reschedule form, prefilled with the current appointment.
  const openReschedule = () => {
    setRsErr(''); setRsConflicts([]);
    const iso = selected?.appointment_datetime || selected?.appointment_at;
    if (iso) {
      // Convert the booking ISO into local YYYY-MM-DD + HH:MM for the inputs.
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      setRsDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setRsTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setRsDate(''); setRsTime('');
    }
    setRsNotify(true);
    setRescheduling(true);
  };

  const closeReschedule = () => {
    setRescheduling(false);
    setRsErr(''); setRsConflicts([]);
  };

  const submitReschedule = async (force = false) => {
    if (!selected) return;
    if (!rsDate || !rsTime) {
      setRsErr('Pick a date and time.');
      return;
    }
    setActionLoading(selected.id);
    setRsErr('');
    try {
      const res = await authFetch(`/api/notary/admin/bookings/${selected.id}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_date: rsDate,
          new_time: rsTime,
          notify_client: rsNotify,
          force_conflict: force,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRsErr(data.detail || 'Reschedule failed.');
        return;
      }
      if (data.slot_taken && !force) {
        setRsConflicts(data.conflicts || []);
        setRsErr('That time conflicts with another booking. Confirm below to book anyway.');
        return;
      }
      setMsg({ type: 'green', text: rsNotify ? 'Rescheduled — client notified.' : 'Rescheduled.' });
      closeReschedule();
      setSelected(null);
      await load();
    } catch (e) {
      setRsErr(e.message || 'Network error.');
    } finally {
      setActionLoading(null);
      setTimeout(() => setMsg(null), 3500);
    }
  };

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);
  const calBroken = calStatus && calStatus.connected === false;

  const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  // Field-shape helpers — backend booking fields are first_name/last_name,
  // notary_type_name, total_price, google_calendar_event_id (legacy fallbacks supported).
  const customerName = (b) => {
    const full = `${b.first_name || ''} ${b.last_name || ''}`.trim();
    return full || b.customer_name || b.name || '—';
  };
  const serviceLabel = (b) =>
    b.notary_type_name || b.service_type || b.service || b.notary_type || '—';
  const totalOf = (b) =>
    b.total_price != null ? b.total_price : (b.total != null ? b.total : null);
  const calEventId = (b) => b.google_calendar_event_id || b.calendar_event_id;

  // ---- Mileage configuration (admin-editable) ----
  const [mileageCfg, setMileageCfg] = useState(null);
  const [mileageDraft, setMileageDraft] = useState({});
  const [savingMileage, setSavingMileage] = useState(false);
  const [showMileage, setShowMileage] = useState(false);

  const loadMileage = async () => {
    try {
      const r = await authFetch('/api/notary/admin/mileage-config');
      if (r.ok) {
        const d = await r.json();
        setMileageCfg(d);
        setMileageDraft({
          free_radius_miles: d.free_radius_miles,
          max_travel_miles: d.max_travel_miles,
          federal_mileage_rate: d.federal_mileage_rate,
          base_address: d.base_address,
        });
      }
    } catch {}
  };
  useEffect(() => { loadMileage(); /* eslint-disable-next-line */ }, []);

  const saveMileage = async () => {
    setSavingMileage(true);
    try {
      const r = await authFetch('/api/notary/admin/mileage-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          free_radius_miles: Number(mileageDraft.free_radius_miles),
          max_travel_miles: Number(mileageDraft.max_travel_miles),
          federal_mileage_rate: Number(mileageDraft.federal_mileage_rate),
          base_address: mileageDraft.base_address,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setMileageCfg(d);
        setMsg({ type: 'green', text: 'Mileage configuration saved.' });
      } else {
        const e = await r.json().catch(() => ({}));
        setMsg({ type: 'red', text: e.detail || 'Save failed' });
      }
    } catch (e) {
      setMsg({ type: 'red', text: e.message });
    } finally {
      setSavingMileage(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  return (
    <PortalV2Layout
      eyebrow="Mobile Notary"
      title="Notary Bookings"
      subtitle={`${bookings.length} bookings on file. Calendar ${calStatus?.connected ? 'connected' : 'not connected'}.`}
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Notary</strong></>}
    >
      {calBroken && (
        <div className="pv2-alert pv2-alert-amber">
          <AlertTriangle size={14} />
          <div>
            <strong>Google Calendar reconnect required.</strong>{' '}
            <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/notary/google-auth`} style={{ color: 'inherit', textDecoration: 'underline' }}>Re-authorize now</a>
            {calStatus?.error && <div style={{ opacity: 0.7, marginTop: 4, fontSize: 12 }}>{calStatus.error}</div>}
          </div>
        </div>
      )}
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
      {msg && <div className={`pv2-alert pv2-alert-${msg.type}`}>{msg.type === 'green' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}<span>{msg.text}</span></div>}

      {/* ============== Mileage configuration card ============== */}
      {mileageCfg && (
        <div className="pv2-card" style={{ marginBottom: 24 }} data-testid="pv2-mileage-config">
          <div className="pv2-card-head" style={{ cursor: 'pointer' }} onClick={() => setShowMileage(s => !s)}>
            <h2 className="pv2-card-title">Mileage Configuration</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="pv2-mono-sm" style={{ color: 'var(--pv2-text-muted)' }}>
                {mileageCfg.free_radius_miles} mi free · ${mileageCfg.federal_mileage_rate}/mi · max {mileageCfg.max_travel_miles} mi
              </span>
              <span className="pv2-mono-sm" style={{ color: 'var(--pv2-red-bright)' }}>{showMileage ? '−' : '+'}</span>
            </div>
          </div>
          {showMileage && (
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Free radius (roundtrip miles)</label>
                <input
                  className="pv2-input"
                  type="number" min="0" step="1"
                  value={mileageDraft.free_radius_miles ?? ''}
                  onChange={(e) => setMileageDraft({ ...mileageDraft, free_radius_miles: e.target.value })}
                  data-testid="pv2-cfg-free-radius"
                />
                <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>
                  Flat allowance against the full roundtrip. Default: {mileageCfg.defaults.free_radius_miles} mi.
                </div>
              </div>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Federal mileage rate ($ / mile)</label>
                <input
                  className="pv2-input"
                  type="number" min="0" step="0.001"
                  value={mileageDraft.federal_mileage_rate ?? ''}
                  onChange={(e) => setMileageDraft({ ...mileageDraft, federal_mileage_rate: e.target.value })}
                  data-testid="pv2-cfg-rate"
                />
                <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>
                  2026 IRS rate: $0.725. Update yearly. Default: ${mileageCfg.defaults.federal_mileage_rate}.
                </div>
              </div>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Max travel (one-way miles)</label>
                <input
                  className="pv2-input"
                  type="number" min="0" step="1"
                  value={mileageDraft.max_travel_miles ?? ''}
                  onChange={(e) => setMileageDraft({ ...mileageDraft, max_travel_miles: e.target.value })}
                  data-testid="pv2-cfg-max-travel"
                />
                <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>
                  Outside this, the booking is blocked. Default: {mileageCfg.defaults.max_travel_miles} mi.
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Base address (mileage origin)</label>
                <input
                  className="pv2-input"
                  value={mileageDraft.base_address ?? ''}
                  onChange={(e) => setMileageDraft({ ...mileageDraft, base_address: e.target.value })}
                  data-testid="pv2-cfg-base-address"
                />
                <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>
                  All distance calculations start here. Default: {mileageCfg.defaults.base_address}
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
                <button
                  onClick={saveMileage}
                  disabled={savingMileage}
                  className="pv2-btn pv2-btn-primary"
                  data-testid="pv2-cfg-save"
                >
                  {savingMileage ? 'Saving…' : 'Save Mileage Settings'}
                </button>
                <div style={{ fontSize: 12, color: 'var(--pv2-text-muted)' }}>
                  Live preview: 50 mi away → 100 mi roundtrip − {mileageDraft.free_radius_miles || 0} mi free = {Math.max(0, 100 - (Number(mileageDraft.free_radius_miles) || 0))} billable mi × ${(Number(mileageDraft.federal_mileage_rate) || 0).toFixed(3)} = <strong>${(Math.max(0, 100 - (Number(mileageDraft.free_radius_miles) || 0)) * (Number(mileageDraft.federal_mileage_rate) || 0)).toFixed(2)}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
        <select className="pv2-select" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ flex: '0 0 200px' }}>
          <option value="all">All Bookings</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <button onClick={cleanupCalendar} disabled={cleaningCal} className="pv2-btn pv2-btn-ghost" data-testid="pv2-calendar-cleanup" title="Re-remove Google Calendar events for bookings that were cancelled">
          <RefreshCw size={12} /> {cleaningCal ? 'Cleaning…' : 'Clean Up Calendar'}
        </button>
      </div>

      <div className="pv2-table-wrap">
        <table className="pv2-table" data-testid="pv2-admin-notary-table">
          <thead>
            <tr><th>Date / Time</th><th>Customer</th><th>Service</th><th>Address</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="pv2-table-empty"><Calendar size={20} style={{ opacity: 0.3, marginBottom: 8 }} />No bookings.</td></tr>}
            {!loading && filtered.map(b => (
              <tr key={b.id} onClick={() => setSelected(b)} style={{ cursor: 'pointer' }}>
                <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{fmtDateTime(b.appointment_datetime || b.appointment_at)}</td>
                <td>{customerName(b)}</td>
                <td style={{ color: 'var(--pv2-text-muted)', fontSize: 12 }}>{serviceLabel(b)}</td>
                <td style={{ color: 'var(--pv2-text-muted)' }}>{b.address || '—'}</td>
                <td>
                  <span className={`pv2-pill pv2-pill-${b.status === 'completed' ? 'green' : (b.status === 'cancelled' || b.status === 'canceled') ? 'red' : 'amber'}`}>
                    {(b.status || 'pending').replace('_',' ')}
                  </span>
                </td>
                <td>{calEventId(b) ? <CheckCircle size={14} color="var(--pv2-green-text)" /> : <AlertTriangle size={14} color="var(--pv2-amber-text)" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawer / detail */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setSelected(null)}>
          <div style={{ width: '100%', maxWidth: 480, height: '100%', background: 'var(--pv2-ink)', borderLeft: '1px solid var(--pv2-ink-line)', padding: 28, overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 className="pv2-display" style={{ fontSize: 22 }}>Booking Detail</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <DetailRow label="Customer" value={customerName(selected)} />
            <DetailRow label="Email" value={selected.email || selected.customer_email} icon={Mail} />
            <DetailRow label="Phone" value={selected.phone || selected.customer_phone} icon={Phone} />
            <DetailRow label="Appointment" value={fmtDateTime(selected.appointment_datetime || selected.appointment_at)} />
            <DetailRow label="Service Type" value={serviceLabel(selected)} />
            <DetailRow label="Address" value={selected.address} icon={MapPin} />
            <DetailRow label="Mileage Fee" value={selected.mileage_fee != null ? `$${Number(selected.mileage_fee).toFixed(2)}` : '—'} />
            <DetailRow label="Total" value={totalOf(selected) != null ? `$${Number(totalOf(selected)).toFixed(2)}` : '—'} />
            <DetailRow label="Notes" value={selected.notes} icon={FileText} />

            <div style={{ marginTop: 24 }}>
              <div className="pv2-mono-sm" style={{ marginBottom: 8 }}>Update status</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STATUSES.map(s => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)} disabled={actionLoading === selected.id || selected.status === s} className={`pv2-btn ${selected.status === s ? 'pv2-btn-primary' : 'pv2-btn-ghost'}`} style={{ padding: '8px 14px', fontSize: 10 }}>
                    {s.replace('_',' ')}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={openReschedule} disabled={actionLoading === selected.id || rescheduling} className="pv2-btn pv2-btn-ghost" data-testid="pv2-booking-reschedule">
                <CalendarClock size={12} /> Reschedule
              </button>
              <button onClick={() => syncCal(selected.id)} disabled={actionLoading === selected.id} className="pv2-btn pv2-btn-ghost"><RefreshCw size={12} /> Sync Calendar</button>
              <button onClick={() => remove(selected.id)} disabled={actionLoading === selected.id} className="pv2-btn pv2-btn-danger"><Trash2 size={12} /> Delete</button>
            </div>

            {rescheduling && (
              <div
                style={{
                  marginTop: 20,
                  padding: 18,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--pv2-ink-line)',
                }}
                data-testid="pv2-booking-reschedule-form"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div className="pv2-mono-sm">Reschedule Booking</div>
                  <button
                    onClick={closeReschedule}
                    style={{ background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}
                    aria-label="Close reschedule form"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div>
                    <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>New date</label>
                    <input
                      type="date"
                      value={rsDate}
                      onChange={(e) => setRsDate(e.target.value)}
                      data-testid="pv2-rs-date"
                      style={{ width: '100%', padding: '10px 12px', background: '#0a0a0a', border: '1px solid var(--pv2-ink-line)', color: '#fff', fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                    />
                  </div>
                  <div>
                    <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>New time</label>
                    <input
                      type="time"
                      value={rsTime}
                      onChange={(e) => setRsTime(e.target.value)}
                      step={900}
                      data-testid="pv2-rs-time"
                      style={{ width: '100%', padding: '10px 12px', background: '#0a0a0a', border: '1px solid var(--pv2-ink-line)', color: '#fff', fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--pv2-text-muted)', cursor: 'pointer', marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={rsNotify}
                    onChange={(e) => setRsNotify(e.target.checked)}
                    data-testid="pv2-rs-notify"
                  />
                  Email the client about the change
                </label>
                {rsErr && (
                  <div className="pv2-alert pv2-alert-amber" style={{ marginBottom: 12 }} data-testid="pv2-rs-err">
                    <AlertTriangle size={13} />
                    <span>{rsErr}</span>
                  </div>
                )}
                {rsConflicts.length > 0 && (
                  <div style={{ marginBottom: 12, padding: 10, background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.25)', fontSize: 12 }}>
                    <div className="pv2-mono-sm" style={{ marginBottom: 6 }}>Conflicting booking(s):</div>
                    {rsConflicts.map((c) => (
                      <div key={c.id} style={{ color: 'var(--pv2-text-muted)' }}>
                        · {fmtDateTime(c.appointment_datetime)} · {c.duration} min
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={closeReschedule} className="pv2-btn pv2-btn-ghost" style={{ padding: '8px 14px', fontSize: 11 }}>
                    Cancel
                  </button>
                  {rsConflicts.length > 0 ? (
                    <button
                      onClick={() => submitReschedule(true)}
                      disabled={actionLoading === selected.id}
                      className="pv2-btn pv2-btn-danger"
                      style={{ padding: '8px 14px', fontSize: 11 }}
                      data-testid="pv2-rs-force"
                    >
                      Book anyway
                    </button>
                  ) : (
                    <button
                      onClick={() => submitReschedule(false)}
                      disabled={actionLoading === selected.id || !rsDate || !rsTime}
                      className="pv2-btn pv2-btn-primary"
                      style={{ padding: '8px 14px', fontSize: 11 }}
                      data-testid="pv2-rs-submit"
                    >
                      {actionLoading === selected.id ? 'Rescheduling…' : 'Confirm reschedule'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </PortalV2Layout>
  );
};

const DetailRow = ({ label, value, icon: Icon }) => (
  <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--pv2-ink-line-soft)' }}>
    <div className="pv2-mono-sm" style={{ marginBottom: 4 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
      {Icon && <Icon size={13} strokeWidth={1.5} color="var(--pv2-text-muted)" />}
      <span>{value || '—'}</span>
    </div>
  </div>
);

export default AdminV2NotaryBookings;
